import React, { useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AccountingPlanningActions from "../AccountingPlanningActions";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import {
  OPERATIONS_JOURNAL_PAGE_SIZE,
  operationsJournalMock,
} from "./mock/operationsJournalMock";
import { DEFAULT_DATA_LAYER, getDataLayerLabel } from "./constants/dataLayers";
import useCorrectionsEditor from "./hooks/useCorrectionsEditor";
import useJournalFilters, { filterJournalRows } from "./hooks/useJournalFilters";
import useSourceJournal from "./hooks/useSourceJournal";
import useColumnOrder from "./hooks/useColumnOrder";
import JournalFilters from "./components/JournalFilters/JournalFilters";
import JournalTable from "./components/JournalTable/JournalTable";
import JournalPagination from "./components/JournalPagination/JournalPagination";
import OperationDetails from "./components/OperationDetails/OperationDetails";
import CorrectionsTable from "./components/CorrectionsTable/CorrectionsTable";
import "./OperationsJournal.css";

function OperationsJournal() {
  const [viewMode, setViewMode] = useState("compact");
  const [currentPage, setCurrentPage] = useState(1);
  const [dataView, setDataView] = useState(DEFAULT_DATA_LAYER);
  const [unit, setUnit] = useState("tenge");
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const { filters, updateFilters, resetFilters } = useJournalFilters(
    searchParams.get("search") || ""
  );
  const isCorrections = dataView === "corrections";
  const isSource = dataView === "source";
  const hasVisibleFilters = isSource
    ? Boolean(
        filters.searchQuery ||
          filters.dateFrom ||
          filters.dateTo ||
          filters.organization ||
          filters.account
      )
    : Boolean(
        filters.searchQuery ||
          filters.dateFrom ||
          filters.dateTo ||
          filters.organization ||
          filters.dataType ||
          filters.source ||
          Object.keys(filters.extraFilters).length
      );
  const { columnOrder, setColumnOrder, visibleColumns } = useColumnOrder();
  const corrections = useCorrectionsEditor();
  const { userData } = useContext(UserContext);
  const authorName = userData
    ? `${userData.surname || ""} ${(userData.name || "").charAt(0)}.`.trim()
    : "Пользователь";
  const exportMenuRef = useRef(null);

  useEffect(() => {
    if (!isExportMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        exportMenuRef.current &&
        !exportMenuRef.current.contains(event.target)
      ) {
        setIsExportMenuOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setIsExportMenuOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isExportMenuOpen]);

  const handleExport = (format) => {
    setIsExportMenuOpen(false);
  };

  const sourceJournal = useSourceJournal({
    enabled: isSource,
    filters,
    page: currentPage,
    pageSize: OPERATIONS_JOURNAL_PAGE_SIZE,
  });

  const filteredRows = isSource
    ? sourceJournal.rows
    : filterJournalRows(operationsJournalMock, filters, {
        getComment: isCorrections
          ? (row) => corrections.getEffectiveFields(row.id).correctionComment
          : undefined,
        ignoreTypeAndSource: isCorrections,
      });

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, dataView]);

  const totalCount = isSource ? sourceJournal.total : filteredRows.length;
  const totalPages = Math.max(
    1,
    Math.ceil(totalCount / OPERATIONS_JOURNAL_PAGE_SIZE)
  );
  const pageStart = (currentPage - 1) * OPERATIONS_JOURNAL_PAGE_SIZE;
  const rows = isSource
    ? filteredRows
    : filteredRows.slice(pageStart, pageStart + OPERATIONS_JOURNAL_PAGE_SIZE);

  return (
    <div className="oj">
      <AccountingPlanningActions>
        <div className="oj-header__actions">
          <button type="button" className="oj-btn oj-btn--primary">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 3.333V12.667M3.333 8H12.667"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            Добавить проводку
          </button>
          <button type="button" className="oj-btn oj-btn--secondary">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 14A6 6 0 1 0 8 2a6 6 0 0 0 0 12Z"
                stroke="currentColor"
                strokeWidth="1.3"
              />
              <path
                d="M8 4.667V8l2 1.333"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            История изменений
          </button>
          <div className="oj-dropdown" ref={exportMenuRef}>
            <button
              type="button"
              className={`oj-btn oj-btn--secondary${
                isExportMenuOpen ? " is-open" : ""
              }`}
              aria-haspopup="menu"
              aria-expanded={isExportMenuOpen}
              onClick={() => setIsExportMenuOpen((open) => !open)}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 10V2.667M8 10L5.333 7.333M8 10l2.667-2.667M2.667 12.667h10.666"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Экспорт
              <svg
                className="oj-dropdown__chevron"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 4.5L6 7.5L9 4.5"
                  stroke="currentColor"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {isExportMenuOpen && (
              <div className="oj-dropdown__menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="oj-dropdown__item"
                  onClick={() => handleExport("pdf")}
                >
                  Экспорт в PDF
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="oj-dropdown__item"
                  onClick={() => handleExport("excel")}
                >
                  Экспорт в Excel
                </button>
              </div>
            )}
          </div>
        </div>
      </AccountingPlanningActions>


      <JournalFilters
        filters={filters}
        onChange={updateFilters}
        onReset={resetFilters}
        hasActiveFilters={hasVisibleFilters}
        searchPlaceholder={
          isSource
            ? "Поиск по содержанию, регистратору..."
            : "Поиск по документу, комментарию..."
        }
        hideTypeAndSource={isCorrections || isSource}
        hideExtraFilters={isSource}
        showAccountFilter={isSource}
        dataView={dataView}
        onDataViewChange={setDataView}
        unit={unit}
        onUnitChange={setUnit}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
      />

      <div className="oj-table-card">
        <div className={`oj-table-banner oj-table-banner--${dataView}`}>
          <span className="oj-table-banner__dot" />
          {getDataLayerLabel(dataView)}
        </div>

        {isCorrections ? (
          <CorrectionsTable
            rows={rows}
            pageStart={pageStart}
            viewMode={viewMode}
            columnOrder={columnOrder}
            unit={unit}
            editor={corrections}
            authorName={authorName}
            selectedId={selectedOperation?.id}
            onOpenOperation={setSelectedOperation}
            onSwitchToExpanded={() => setViewMode("expanded")}
          />
        ) : (
          <JournalTable
            rows={rows}
            pageStart={pageStart}
            viewMode={viewMode}
            visibleColumns={visibleColumns}
            unit={unit}
            isSource={isSource}
            selectedId={selectedOperation?.id}
            onRowClick={setSelectedOperation}
          />
        )}

        {isSource &&
          !sourceJournal.error &&
          rows.length === 0 &&
          (sourceJournal.loading || !sourceJournal.loaded) && (
            <div className="oj-empty-state">Загрузка журнала...</div>
          )}

        {isSource && sourceJournal.error && (
          <div className="oj-empty-state">{sourceJournal.error}</div>
        )}

        {rows.length === 0 &&
          !(
            isSource &&
            (sourceJournal.loading || sourceJournal.error || !sourceJournal.loaded)
          ) && (
            <div className="oj-empty-state">
              По выбранным фильтрам операций не найдено
            </div>
          )}

        <JournalPagination
          page={currentPage}
          totalPages={totalPages}
          shown={rows.length}
          total={totalCount}
          onChange={setCurrentPage}
        />
      </div>

      <OperationDetails
        operation={selectedOperation}
        unit={unit}
        dataLayer={dataView}
        editor={corrections}
        authorName={authorName}
        onClose={() => setSelectedOperation(null)}
      />
    </div>
  );
}

export default OperationsJournal;
